import { parseSort, type SortValue, sortValue } from "@kopi/contracts/client";
import type { DataTableSorting as Sorting } from "@kopi/ui/components/data-table";

/**
 * The `?sort=` search param of a list route (R1-09d): kept when it is one of the list's sorts, dropped
 * otherwise (a stale or hand-edited URL falls back to the list's default order).
 */
export function sortParam<F extends string>(fields: readonly F[], raw: unknown): SortValue<F> | undefined {
  const allowed = fields.flatMap((field) => [sortValue(field, "asc"), sortValue(field, "desc")]);
  return allowed.find((sort) => sort === raw);
}

/** The table's sorting for a `sort` param; without one, the list's default order shows as sorted. */
export function sortingOf<F extends string>(sort: SortValue<F> | undefined, fallback: SortValue<F>): Sorting {
  const { field, direction } = parseSort(sort ?? fallback);
  return [{ id: field, desc: direction === "desc" }];
}

/** The `sort` param for the table's sorting: undefined for the default order, so the URL stays clean. */
export function sortFrom<F extends string>(
  fields: readonly F[],
  sorting: Sorting,
  fallback: SortValue<F>,
): SortValue<F> | undefined {
  const [first] = sorting;
  if (!first) return undefined;
  const sort = sortParam(fields, sortValue(first.id, first.desc ? "desc" : "asc"));
  return sort === fallback ? undefined : sort;
}

/** A list route's `validateSearch`: only `sort`, and only one of the list's sorts. */
export function sortSearch<F extends string>(fields: readonly F[]) {
  return (search: Record<string, unknown>): { sort?: SortValue<F> } => {
    const sort = sortParam(fields, search.sort);
    return sort ? { sort } : {};
  };
}

/**
 * `DataTable`'s `sorting` and `onSortingChange` for a list whose sort lives in the URL: `apply` writes
 * the picked sort back (undefined for the default order).
 */
export function urlSorting<F extends string>(
  fields: readonly F[],
  fallback: SortValue<F>,
  sort: SortValue<F> | undefined,
  apply: (sort: SortValue<F> | undefined) => void,
) {
  return {
    sorting: sortingOf(sort, fallback),
    onSortingChange: (sorting: Sorting) => apply(sortFrom(fields, sorting, fallback)),
  };
}
